<template>
  <ValidationObserver
    tag="form"
    @submit="onSubmit"
    ref="form"
    :class="{
      'f-border':
        formResource.styling?.border === undefined ||
        formResource.styling.border === true,
    }"
    :id="$id('fapi-form-form')"
  >
    <div
      v-if="hasLoadingError"
      class="fapi-loading-error-alert f-bg-red-100 f-text-red-800 f-p-4 f-rounded-lg f-mb-4 f-text-center"
    >
      <p>⚠️ {{ $t("async_component.critical_component_failed") }}</p>
      <button
        class="fapi-reload-button f-mt-2 f-bg-red-600 f-text-white f-py-2 f-px-4 f-rounded hover:f-bg-red-700"
        @click="reloadPage"
      >
        {{ $t("async_component.reload_button") }}
      </button>
    </div>
    <Alert
      ref="formError"
      :message="$t(formError)"
      :scrollIntoView="true"
      v-if="formError !== null"
    ></Alert>

    <input
      type="text"
      name="website"
      v-model="formData.honeypot"
      tabindex="-1"
      autocomplete="off"
      class="fapi-honeypot"
      aria-hidden="true"
    />

    <OrderIntentData
      v-if="isTwoStepForm && !orderIntentSubmitted"
      class="fapi-form-order-intent-data"
      :formResource="formResource"
      :styling="formResource.styling"
      :templateSettings="formResource.template_settings"
      :country="formData.address.country"
      :shippingCountry="formData.shipping_address.country"
      :formData="formData"
      :forceFullInvoice="forceFullInvoice"
      :fieldSettings="formResource.field_settings"
      :showShippingDetails="showShippingMethods"
      :allowShippingAddress="formResource.allow_shipping_address"
      :showNeededShippingAddressByItem="showShippingMethodsByItem"
      :isSubmitting="isSubmitting"
      @isAddressShowChanged="isAddressShowHandle"
      @basicDataChanged="updateBasicData"
      @countryChanged="countryChanged"
      @addressChanged="updateAddress"
      @shippingAddressChanged="shippingAddressChanged"
      @purposeChanged="purposeChanged"
      @triggerOrderIntent="triggerOrderIntent"
    ></OrderIntentData>

    <ItemGroups
      v-if="!isTwoStepForm || orderIntentSubmitted"
      class="fapi-form-items"
      :itemGroups="itemGroups"
      :currency="formData.currency"
      :formResource="formResource"
      :selectedItems="formData.selected_items"
      :orderBumps="orderBumps"
      :isVatPayer="
        serviceLocator.isVatPayer.isVatPayer(formData.address.country)
      "
      :allowedPeriods="allowedPeriods"
      :selectedPeriod="formData.period"
      :repayment="formData.repayment"
      @itemDataChanged="itemDataChanged"
      @currencyChanged="currencyChanged"
      @orderBumpChanged="orderBumpChanged"
      @periodChanged="periodChanged"
    ></ItemGroups>

    <BasicData
      v-if="!isTwoStepForm || orderIntentSubmitted"
      class="fapi-form-basic-data"
      :formResource="formResource"
      :templateSettings="formResource.template_settings"
      :country="formData.address.country"
      :shippingCountry="formData.shipping_address.country"
      :formData="formData"
      :forceFullInvoice="forceFullInvoice"
      :fieldSettings="formResource.field_settings"
      :showShippingDetails="showShippingMethods"
      :shippingMethods="shippingMethods"
      :allowShippingAddress="formResource.allow_shipping_address"
      :showNeededShippingAddressByItem="showShippingMethodsByItem"
      @isAddressShowChanged="isAddressShowHandle"
      @basicDataChanged="updateBasicData"
      @countryChanged="countryChanged"
      @addressChanged="updateAddress"
      @shippingAddressChanged="shippingAddressChanged"
    ></BasicData>

    <CustomFields
      class="fapi-form-custom-fields"
      :formResource="formResource"
      :templateSettings="formResource.template_settings"
      v-if="
        formResource.custom_fields.length > 0 &&
        (!isTwoStepForm || orderIntentSubmitted)
      "
      @customFieldUpdated="customFieldUpdated"
    ></CustomFields>

    <ShippingMethods
      v-if="
        toggleShippingMethods &&
        formResource.shipping_methods.length > 0 &&
        (!isTwoStepForm || orderIntentSubmitted)
      "
      class="fapi-form-shipping-methods"
      :formResource="formResource"
      :templateSettings="formResource.template_settings"
      :isAddressShow="isAddress"
      :showShippingMethods="showShippingMethods"
      :shippingMethods="shippingMethods"
      :currency="formData.currency"
      :addressCountry="formData.address.country"
      :shippingCountry="formData.shipping_address.country"
      :withShippingAddress="formData.with_shipping_address"
      :fullInvoice="formData.full_invoice"
      :order="order"
      @shippingMethodChanged="shippingMethodChanged"
      @isEligibleForFreeShipping="eligibleForFreeShippingChanged($event)"
    ></ShippingMethods>

    <PaymentMethods
      v-if="(!isTwoStepForm || orderIntentSubmitted) && togglePaymentMethods"
      class="fapi-form-payment-methods"
      :paymentMethods="formResource.payment_methods"
      :templateSettings="formResource.template_settings"
      :currency="formData.currency"
      :country="formData.address.country"
      :shippingCountry="formData.shipping_address.country"
      :phoneRequiredOrFilled="
        formResource.require_field_phone || formData.phone !== null
      "
      :orderPriceIncVat="order.totalPriceIncludingVat"
      :formResource="formResource"
      :collectOnDeliveryPaymentMethodItem="collectOnDeliveryPaymentMethodItem"
      :stripe="stripe"
      :formData="formData"
      :isPhysicalItemSelected="isPhysicalItemSelected"
      @stripeElements="onStripeElements"
      @paymentMethodChanged="paymentMethodChanged"
    ></PaymentMethods>
    <Result
      v-if="!isTwoStepForm || orderIntentSubmitted"
      class="fapi-form-result"
      :formResource="formResource"
      :styling="formResource.styling"
      :order="order"
      :formData="formData"
      :isSubmitting="isSubmitting"
      :discountCodeState="discountCodeState"
      :isProcessing="isDiscountCodeProcessing"
      :discountCode="discountCode"
      :repayment="formData.repayment"
      :repaymentCount="formData.repayment_count || 0"
      :installments="installments"
      :currency="formData.currency"
      :isChangeNameRepaymentToPayment="
        Boolean(
          formResource.settings?.change_name_repayment_to_payment ?? false,
        )
      "
      @tryToApplyDiscountCode="tryToApplyDiscountCode"
      @discountCodeChanged="discountCodeChanged"
      @purposeChanged="purposeChanged"
      @termsChanged="termsChanged"
      @repaymentChanged="repaymentChanged"
      @installmentsChanged="installmentsChanged"
      @resultContainerIdAssigned="resultContainerId = $event.id"
    />

    <FloatPrice
      class="fapi-form-float-price"
      v-if="
        showFloat &&
        resultContainerId &&
        (!isTwoStepForm || orderIntentSubmitted) &&
        formResource.styling?.float_price_position !== 'none'
      "
      :formResource="formResource"
      :currency="formData.currency"
      :order="order"
      :discountCode="discountCode"
      :repayment="formData.repayment"
      :repaymentCount="formData.repayment_count || 0"
      :resultContainerId="resultContainerId"
      :showPricesIncludingVat="formResource.show_prices_including_vat"
      :installments="installments"
      :isChangeNameRepaymentToPayment="
        Boolean(
          formResource.settings?.change_name_repayment_to_payment ?? false,
        )
      "
    />
  </ValidationObserver>
</template>

<script lang="ts">
import FormResource from "@/module/Form/Resources/FormResource";
import OrderIntentData from "@/components/Form/Basic/OrderIntentData.vue";
import BasicData from "@/components/Form/Basic/BasicData.vue";
import FormItem from "@/module/FormItem";
import FormItemBuilder from "@/module/FapiFormDataProvider";
import PaymentMethods from "@/components/Form/PaymentMethods/PaymentMethods.vue";
import ItemGroups from "@/components/Form/Items/ItemGroups.vue";
import Result from "@/components/Form/Result/Result.vue";
import FormItemGroup from "@/module/FormItemGroup";
import FormData from "@/module/FormData";
import SelectedItem from "@/module/Form/SelectedItem";
import { ValidationObserver } from "vee-validate";
import NumberHelper from "@/module/NumberHelper";
import UrlParser from "@/module/Utils/UrlParser";
import PostRedirect from "@/module/Utils/PostRedirect";
import ScriptsLoader from "@/module/Utils/ScriptsLoader";
import TrackingCodeLoader from "@/module/Utils/TrackingCodeLoader";
import FormOrderBump from "@/module/FormOrderBump";
import FormHelper from "@/module/FormHelper";
import FormShippingItem from "@/module/FormShippingItem";
import { DiscountCodeResponse } from "@/module/DiscountCode/DiscountCodeResponse";
import DiscountCode from "@/module/DiscountCode/DiscountCode";
import { DiscountType } from "@/module/DiscountCode/DiscountType";
import ServiceLocator from "@/module/Utils/ServiceLocator";
import SelectedFormItemWithCount from "@/module/SelectedFormItemWithCount";
import LocaleLoader from "@/module/Utils/LocaleLoader";
import OrderPrices from "@/module/Order/OrderPrices";
import { ItemType } from "@/module/Item/ItemType";
import Vue, { PropType } from "vue";
import Alert from "@/components/Form/Alert.vue";
import FormPaymentMethodItem from "@/module/FormPaymentMethodItem";
import { formatDateTimeOrNull } from "@/module/Utils/DateTimeHelpers";
import {
  loadStripe,
  Stripe,
  StripeElements,
  StripeElementLocale,
} from "@stripe/stripe-js";
import { getCookieValueByName } from "@/module/Utils/functions";
import { VatMode } from "@/module/Form/VatDeterminer/VatMode";
import { OssType } from "@/module/Form/Oss/OssType";
import retryableAsyncComponent from "@/module/Utils/RetryableAsyncComponent";
import EventBus from "@/module/Utils/EventBus";

interface ComponentData {
  serviceLocator: ServiceLocator;

  itemGroups: Array<FormItemGroup>;
  orderBumps: Array<FormOrderBump>;
  formItems: Array<FormItem>;
  upsellItems: Array<FormItem>;
  shippingMethods: Array<FormShippingItem>;
  discountCode: DiscountCode | null;
  shippingItem: FormItem | undefined | null;

  discountCodeCode: string;
  formError: string | null;
  discountCodeState: boolean | null;
  isDiscountCodeProcessing: boolean;
  isSubmitting: boolean;
  isAddress: boolean;
  showShippingMethods: boolean;
  showShippingMethodsByItem: boolean;
  isPhysicalItemSelected: boolean;
  togglePaymentMethods: boolean;
  toggleShippingMethods: boolean;
  showFloat: boolean;

  formData: FormData;
  order: OrderPrices;

  allowedPeriods: Array<string>;
  forceFullInvoice: boolean;
  calculationRunning: boolean;

  collectOnDeliveryPaymentMethodItem: FormPaymentMethodItem | null;
  paymentMethodItem: FormPaymentMethodItem | null;

  stripe: Stripe | null;
  stripeElements: StripeElements | null;

  installments: InstallmentItem[];

  resultContainerId: string | null;
  isTwoStepForm: boolean;
  orderIntentSubmitted: boolean;
  twoStepRedirect: boolean;
  twoStepRedirectUrl: string | undefined;

  hasLoadingError: boolean;
  eventCallback: any;
}

export default Vue.extend({
  name: "FapiForm",

  components: {
    FloatPrice: () =>
      import(
        /* webpackChunkName: "float-price" */ "@/components/Form/FloatPrice.vue"
      ),
    ShippingMethods: retryableAsyncComponent(
      () =>
        import(
          /* webpackChunkName: "shipping-methods" */ "@/components/Form/ShippingMethods/ShippingMethods.vue"
        ),
    ),
    CustomFields: retryableAsyncComponent(
      () =>
        import(
          /* webpackChunkName: "custom-fields" */ "@/components/Form/CustomFields/CustomFields.vue"
        ),
    ),
    Alert,
    Result,
    ItemGroups,
    PaymentMethods,
    BasicData,
    OrderIntentData,
    ValidationObserver,
  },

  props: {
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    localeLoader: {
      type: Object as PropType<LocaleLoader>,
      required: true,
    },
    securityToken: {
      type: Object as PropType<{
        token: string;
        expiration: string;
      }>,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      serviceLocator: new ServiceLocator(this.formResource),

      itemGroups: [],
      orderBumps: [],
      formItems: [],
      upsellItems: [],
      shippingMethods: [],
      discountCode: null,
      shippingItem: null,

      discountCodeCode: "",
      formError: null,
      discountCodeState: null,
      isDiscountCodeProcessing: false,
      isSubmitting: false,
      isAddress: false,
      showShippingMethods: false,
      showShippingMethodsByItem: false,
      isPhysicalItemSelected: false,
      togglePaymentMethods: false,
      toggleShippingMethods: false,
      showFloat: false,

      formData: {
        email: "",
        phone: null,
        first_name: null,
        last_name: null,
        company: null,
        ico: null,
        dic: null,
        ic_dph: null,
        currency: "CZK",
        address: {
          street: null,
          city: null,
          zip: null,
          country: null,
        },
        shipping_address: {
          first_name: null,
          last_name: null,
          company: null,
          street: null,
          city: null,
          zip: null,
          country: null,
        },
        full_invoice: true,
        with_shipping_address: false,
        selected_items: [],
        order_bump_selected_items: [],
        custom_fields: {},
        discount_code_id: null,
        google_analytics_client_id: null,
        form_url: "",
        form_title: "",
        session_id: null,
        shipping_method_id: null,
        shipping_metadata: null,
        purposes: [],
        terms_accepted: false,
        terms_text: null,
        repayment: false,
        repayment_count: 0,
        payment_type: null,
        bank: null,
        period: null,
        pending: false,
        upsells_enabled: false,
        form_loaded: new Date(),
        form_first_interaction: null,
        form_fill_duration: null,
        honeypot: "",

        tracking: {
          browser: {} as any,
          facebook: {} as any,
          ga4: {} as any,
          fapi: {} as any,
        },
      },

      order: {
        originalPrice: 0,
        originalPriceIncVat: 0,
        totalPriceIncludingVat: 0,
        discount: 0,
        discountWithoutVat: 0,
        totalPrice: 0,
        isVatPayer: false,
        isEligibleForFreeShipping: false,
        totalPriceWoShippingIncludingVat: 0,
        totalPriceWoShipping: 0,
        disableRepaymentTotalPriceIncVat: 0,
        disableRepaymentTotalPrice: 0,
      },

      allowedPeriods: [],
      forceFullInvoice: false,
      calculationRunning: false,

      collectOnDeliveryPaymentMethodItem: null,
      paymentMethodItem: null,

      stripe: null,
      stripeElements: null as StripeElements | null,

      installments: [] as InstallmentItem[],

      resultContainerId: null,

      isTwoStepForm:
        true ===
        this.formResource?.template_settings?.container_settings
          ?.is_two_step_form,

      orderIntentSubmitted: false,

      twoStepRedirect:
        "redirect" ===
        this.formResource?.template_settings?.container_settings
          ?.two_step_form_type,

      twoStepRedirectUrl:
        this.formResource?.template_settings?.container_settings
          ?.two_step_redirect_url,

      hasLoadingError: false,
      eventCallback: null as any,
    };
  },

  methods: {
    async triggerOrderIntent(): Promise<void> {
      const formEle = this.$refs.form as InstanceType<
        typeof ValidationObserver
      > &
        HTMLElement;
      const promise = formEle.validate();

      this.$nextTick(function () {
        this.isSubmitting = true;
        promise.then(this.processOrderIntentValidation);
      });
    },

    async processOrderIntentValidation(success: boolean): Promise<void> {
      if (!success) {
        this.rollToError();
        return;
      }

      if (this.formData.honeypot) {
        this.addFormErrorMessage("form.server_error.honeypot_detected");

        return;
      }

      let response = await this.sendOrderIntentToFapi();

      if ("success" in response && response.success) {
        await this.serviceLocator.statisticsReporter
          .incrementOrderIntent(this.formData)
          .catch((e) => {
            console.warn(e);
          });

        if (
          this.twoStepRedirect === true &&
          this.twoStepRedirectUrl !== undefined
        ) {
          window.location.href = this.parseFirstStepDataIntoUrl(
            this.twoStepRedirectUrl,
          );
        } else {
          this.isSubmitting = false;
          this.orderIntentSubmitted = true;
        }
      } else if ("error" in response) {
        this.addFormErrorMessage(response.error);
      }
    },

    parseFirstStepDataIntoUrl(url: string): string {
      const urlObject = new URL(url);
      let result =
        urlObject.protocol + "//" + urlObject.host + urlObject.pathname;
      result += urlObject.search === "" ? "?" : urlObject.search + "&";

      const data = {
        "fapi-form-name": this.formData.first_name,
        "fapi-form-surname": this.formData.last_name,
        "fapi-form-email": this.formData.email,
        "fapi-form-mobil": this.formData.phone,
      };

      const jsonData = JSON.stringify(data);
      const base64EncodedData = btoa(encodeURIComponent(jsonData));
      result += `fapi-form-data=${base64EncodedData}`;

      return result;
    },

    skipToSecondStep(): boolean {
      if (!this.isTwoStepForm) {
        return false;
      }

      var data = [
        this.formData.first_name,
        this.formData.last_name,
        this.formData.email,
        this.formData.phone,
      ];

      var returnValue = false;

      data.forEach(function (value) {
        if (value !== null && value != "") {
          returnValue = true;
        }
      });

      return returnValue;
    },

    sendOrderIntentToFapi(): any {
      const data: any = { ...this.formData };

      return this.serviceLocator.createOrderIntentRequest
        .create(data)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          return {
            success: false,
            error: "form.server_error.default",
            error_data: error,
          };
        });
    },

    async updateBasicData(value: { [key: string]: string }): Promise<void> {
      this.formData = {
        ...this.formData,
        ...value,
      };

      for (const index of ["ico", "dic", "ic_dph"]) {
        if (value.hasOwnProperty(index)) {
          await this.recalculateItems();
          await this.recalculateOrder();
        }
      }

      if (
        value.hasOwnProperty("with_shipping_address") &&
        !value.with_shipping_address
      ) {
        this.formData.shipping_address = {
          first_name: null,
          last_name: null,
          company: null,
          street: null,
          city: null,
          zip: null,
          country: null,
        };
      }
    },

    isAddressShowHandle(newValue: boolean) {
      this.isAddress = newValue;
    },

    async shippingMethodChanged(value: object | any | null): Promise<void> {
      if (value !== null) {
        this.formData = {
          ...this.formData,
          ...value,
        };
        this.shippingItem = value.shipping_form_item;
      } else {
        this.formData.shipping_method_id = null;
        this.formData.shipping_metadata = null;
        this.shippingItem = null;
      }

      await this.recalculateItems();
      await this.recalculateOrder();
    },

    async eligibleForFreeShippingChanged(value: any): Promise<void> {
      if (this.formData.shipping_method_id == value.shipping_method_id) {
        this.order.isEligibleForFreeShipping =
          value.is_eligible_for_free_shipping;
      }

      await this.recalculateItems();
      await this.recalculateOrder();
    },

    updateAddress(value: object): void {
      this.formData.address = {
        ...this.formData.address,
        ...value,
      };
    },

    async shippingAddressChanged(value: object): Promise<void> {
      this.formData.shipping_address = {
        ...this.formData.shipping_address,
        ...value,
      };

      if (value.hasOwnProperty("country")) {
        await this.recalculateItems();
        await this.recalculateOrder();
      }
    },

    async countryChanged(value: string | null): Promise<void> {
      this.determinateCurrency(value);
      this.formData.address.country = value;
      await this.recalculateItems();
      await this.recalculateOrder();
    },

    customFieldUpdated(value: object): void {
      this.formData.custom_fields = {
        ...this.formData.custom_fields,
        ...value,
      };
    },

    async paymentMethodChanged(
      value: {
        payment_type: string;
        bank: string | null;
        payment_method_item: FormPaymentMethodItem | null;
      } | null,
    ): Promise<void> {
      if (value === null) {
        this.formData.payment_type = null;
        this.formData.bank = null;
        this.paymentMethodItem = null;
      } else {
        this.formData.payment_type = value.payment_type;
        this.formData.bank = value.bank;
        this.paymentMethodItem = value.payment_method_item;
      }

      await this.recalculateItems();
      await this.recalculateOrder();
    },

    async repaymentChanged(value: boolean): Promise<void> {
      this.formData.repayment = value;
      await this.recalculateItems();
      await this.recalculateOrder();
    },

    async installmentsChanged(repaymentCount: number): Promise<void> {
      this.formData.repayment_count = repaymentCount;
      await this.recalculateItems();
      await this.recalculateOrder();
    },

    purposeChanged(value: any): void {
      const purpose = this.formData.purposes.find((item) => {
        return item.form_purpose_id === value.form_purpose_id;
      });

      if (purpose === undefined) {
        this.formData.purposes.push(value);
      } else {
        purpose.checked = value.checked;
        purpose.text = value.text;
      }
    },

    termsChanged(value: {
      terms_accepted: boolean;
      terms_text: string | null;
    }): void {
      this.formData.terms_accepted = value.terms_accepted;
      this.formData.terms_text = value.terms_text;
    },

    async currencyChanged(value: string): Promise<void> {
      const oldCurrency = this.formData.currency;
      this.formData.currency = value;
      this.determinateCurrency(this.formData.address.country);

      if (oldCurrency !== this.formData.currency) {
        await this.recalculateItems();
        await this.recalculateOrder();
      }
    },

    discountCodeChanged(value: string): void {
      this.discountCodeState = null;
      this.discountCodeCode = value;
    },

    async tryToApplyDiscountCode(): Promise<void> {
      if (this.discountCodeCode === "") {
        this.discountCodeState = null;
        this.formData.discount_code_id = null;
        this.discountCode = null;
        this.discountCodeCode = "";

        await this.recalculateItems();
        await this.recalculateOrder();

        return;
      }

      this.isDiscountCodeProcessing = true;

      return this.serviceLocator.findDiscountCode
        .get(this.discountCodeCode)
        .then((data) => {
          this.applyDiscountCode(data);
        })
        .catch(() => {
          this.failedToApplyDiscountCode();
        });
    },

    async applyDiscountCode(data: DiscountCodeResponse): Promise<void> {
      const discountCodeData = data.discount_code;

      if (!data.applicable || discountCodeData === null) {
        this.formData.discount_code_id = null;
        this.discountCode = null;
        this.discountCodeState = false;
      } else {
        this.discountCode = new DiscountCode(
          discountCodeData.id,
          discountCodeData.type,
          discountCodeData.percent_discount,
          discountCodeData.discount_amounts,
          discountCodeData.including_vat,
        );
        this.formData.discount_code_id = this.discountCode.id;
        this.discountCodeState = true;
      }

      await this.recalculateItems();
      await this.recalculateOrder();
      this.isDiscountCodeProcessing = false;
    },

    failedToApplyDiscountCode(): void {
      this.isDiscountCodeProcessing = false;
      this.discountCode = null;
      this.formData.discount_code_id = null;
      this.discountCodeState = false;
    },

    async itemDataChanged(data: SelectedItem): Promise<void> {
      const index = this.formData.selected_items.findIndex(
        (ele) => ele.id === data.id,
      );

      if (index === -1) {
        this.formData.selected_items.push(data);
      } else {
        this.formData.selected_items[index] = data;
      }

      await this.recalculateItems();
      await this.recalculateOrder();
      this.processShowShippingMethods();
    },

    async orderBumpChanged(data: SelectedItem): Promise<void> {
      const index = this.formData.order_bump_selected_items.findIndex(
        (ele) => ele.id === data.id,
      );

      if (index === -1 && !data.selected) {
        return;
      }

      if (!data.selected) {
        this.formData.order_bump_selected_items.splice(index, 1);
        await this.recalculateItems();
        await this.recalculateOrder();
        this.processShowShippingMethods();

        return;
      }

      if (index === -1) {
        this.formData.order_bump_selected_items.push(data);
      } else {
        this.formData.order_bump_selected_items[index] = data;
      }

      await this.recalculateItems();
      await this.recalculateOrder();
      this.processShowShippingMethods();
    },

    periodChanged(period: string): void {
      this.formData.period = period;
    },

    async recalculateOrder(): Promise<void> {
      let totalPriceIncludingVat = 0;
      let originalPrice = 0;
      let originalPriceIncVat = 0;
      let totalPrice = 0;
      let discount = 0;
      let discountPrice = 0;
      let discountWithOutVat = 0;
      let disableRepaymentTotalPrice = 0;
      let disableRepaymentTotalPriceIncVat = 0;

      for (const selectedFormItemWithCount of this.selectedFormItemsWithCount()) {
        const item = selectedFormItemWithCount.formItem;
        const count = selectedFormItemWithCount.count;

        if (item.metadata.is_disable_repayment) {
          const disableRepaymentUnitItemTotalPriceIncludingVat =
            item.currentDiscountedPriceIncVat !== null
              ? item.currentDiscountedPriceIncVat
              : item.currentPriceIncVat;
          const disableRepaymentUnitItemTotalPrice =
            item.currentDiscountedPrice !== null
              ? item.currentDiscountedPrice
              : item.currentPrice;

          disableRepaymentTotalPrice +=
            disableRepaymentUnitItemTotalPrice * count;
          disableRepaymentTotalPriceIncVat +=
            disableRepaymentUnitItemTotalPriceIncludingVat * count;
        }

        const unitItemTotalPriceIncludingVat =
          item.currentDiscountedPriceIncVat !== null
            ? item.currentDiscountedPriceIncVat
            : item.currentPriceIncVat;
        const unitItemTotalPrice =
          item.currentDiscountedPrice !== null
            ? item.currentDiscountedPrice
            : item.currentPrice;

        originalPrice += item.originalPrice * count;
        originalPriceIncVat += item.originalPriceIncVat * count;
        totalPriceIncludingVat += unitItemTotalPriceIncludingVat * count;
        totalPrice += unitItemTotalPrice * count;
        discount +=
          (item.currentDiscountedPriceIncVat === null
            ? 0
            : item.currentPriceIncVat - item.currentDiscountedPriceIncVat) *
          count;
      }

      if (this.paymentMethodItem !== null) {
        const paymentItem = this.paymentMethodItem.findFormItemByCountryCode(
          this.formData.shipping_address.country ??
            this.formData.address.country,
        );

        if (paymentItem !== null) {
          totalPrice += paymentItem.currentPrice;
          originalPrice += paymentItem.originalPrice;
          originalPriceIncVat += paymentItem.originalPriceIncVat;
          totalPriceIncludingVat += paymentItem.currentPriceIncVat;
        }
      }

      if (
        this.discountCode !== null &&
        this.discountCode.type === DiscountType.CASH
      ) {
        let discountAmount =
          this.discountCode.getDiscountAmount(this.formData.currency) || 0;

        let discountIncludingVat = this.discountCode.isIncludingVat();

        const prices = this.getPrices();

        const sortedVatRates = Object.keys(prices).sort(
          (a, b) => Number(b) - Number(a),
        );

        for (const vatRate of sortedVatRates) {
          const price = prices[vatRate];
          let result;

          if (discountIncludingVat) {
            result = NumberHelper.discountWithVat(
              price,
              Number(vatRate),
              discountAmount,
            );
          } else {
            result = NumberHelper.discountWithoutVat(
              price,
              Number(vatRate),
              discountAmount,
            );
          }

          discountPrice += result.discount;
          discountWithOutVat += result.discountWoVat;
          discountAmount = result.remainingDiscount;

          if (discountAmount <= 0) {
            break;
          }
        }

        discount = discountPrice;
      }

      totalPriceIncludingVat -= discountPrice;
      totalPrice -= discountWithOutVat;

      discount = NumberHelper.round(discount);
      discountWithOutVat = NumberHelper.round(discountWithOutVat);
      const totalPriceWoShipping = totalPrice;
      const totalPriceWoShippingIncludingVat = totalPriceIncludingVat;

      if (this.shippingItem && !this.order.isEligibleForFreeShipping) {
        if (
          this.shippingItem.metadata &&
          this.shippingItem.metadata.is_disable_repayment
        ) {
          disableRepaymentTotalPrice += this.shippingItem.currentPrice;
          disableRepaymentTotalPriceIncVat +=
            this.shippingItem.currentPriceIncVat;
        }
        totalPrice += this.shippingItem.currentPrice;
        totalPriceIncludingVat += this.shippingItem.currentPriceIncVat;
        originalPrice += this.shippingItem.currentPrice;
        originalPriceIncVat += this.shippingItem.currentPriceIncVat;
      }

      totalPriceIncludingVat = Math.max(totalPriceIncludingVat, 0);
      totalPrice = Math.max(totalPrice, 0);

      if (this.formData.repayment) {
        if (disableRepaymentTotalPrice !== 0) {
          totalPriceIncludingVat -= disableRepaymentTotalPriceIncVat;
          totalPrice -= disableRepaymentTotalPrice;
        }
      }

      const isReverseCharge = false;

      if (
        !isReverseCharge &&
        this.formResource.settings.vat_mode === VatMode.IDENTIFIED_PERSON &&
        this.formResource.settings.use_moss_for_non_vat_payer_eu_companies ===
          OssType.NATIVE_VAT
      ) {
        this.order.isVatPayer = false;
      } else {
        this.order.isVatPayer = this.serviceLocator.isVatPayer.isVatPayer(
          this.formData.address.country,
        );
      }
      this.order.totalPriceWoShipping = totalPriceWoShipping;
      this.order.totalPriceWoShippingIncludingVat =
        totalPriceWoShippingIncludingVat;
      this.order.discount = discount;
      this.order.discountWithoutVat = discountWithOutVat;
      this.order.originalPrice = originalPrice;
      this.order.originalPriceIncVat = originalPriceIncVat;
      this.recalculateRepayments();
      this.calculateRoundItem(
        totalPrice,
        totalPriceIncludingVat,
        disableRepaymentTotalPrice,
        disableRepaymentTotalPriceIncVat,
        this.installments,
      );

      let maxUpsellItemPrice = 0;

      for (const upsellItem of this.upsellItems) {
        const price =
          upsellItem.currentDiscountedPrice || upsellItem.currentPriceIncVat;

        if (price <= maxUpsellItemPrice) {
          continue;
        }

        maxUpsellItemPrice = price;
      }

      //todo fix
      // await this.serviceLocator.reverseChargeDeterminer.detectReverseCharge(
      //   this.serviceLocator.isVatPayer.isVatPayer(
      //     this.formData.address.country
      //   ),
      //   this.formData.address.country,
      //   this.formData.shipping_address.country,
      //   this.formData.ico,
      //   this.formData.dic,
      //   this.formData.ic_dph
      // );

      // const totalAmount = this.formData.repayment
      //   ? this.order.totalPriceIncludingVat *
      //     (this.formResource.repayment_count as number)
      //   : this.order.totalPriceIncludingVat;

      const isSimplifiedAllowed = true;
      // await this.serviceLocator.simplifiedInvoiceChecker.isAllowed(
      //   totalAmount + maxUpsellItemPrice,
      //   this.formData.currency,
      //   this.formResource.user_country,
      //   this.serviceLocator.exchangeRateProvider
      // );

      if (!isSimplifiedAllowed || isReverseCharge) {
        this.formData.full_invoice = true;
        this.forceFullInvoice = true;
      } else {
        this.forceFullInvoice = false;
      }
    },

    recalculateRepayments(): void {
      if (!this.formData.repayment) {
        return;
      }

      const repaymentCount = this.formData.repayment_count;
      if (!repaymentCount) {
        return;
      }

      const fractions = FormHelper.createFractionMap(repaymentCount);

      this.installments = [];
      for (let i = 0; i < repaymentCount; i++) {
        this.installments[i] = {
          installmentNumber: i + 1,
          installmentAmountIncVat: 0,
          installmentAmount: 0,
          savedAmount: 0,
        };
      }

      for (const selectedFormItemWithCount of this.selectedFormItemsWithCount()) {
        const item = selectedFormItemWithCount.formItem;
        const count = selectedFormItemWithCount.count;

        if (item.metadata.is_disable_repayment) {
          continue;
        }

        const unitItemPriceIncVat =
          item.currentDiscountedPriceIncVat !== null
            ? item.currentDiscountedPriceIncVat
            : item.currentPriceIncVat;

        const unitItemTotalPrice =
          item.currentDiscountedPrice !== null
            ? item.currentDiscountedPrice
            : item.currentPrice;

        const totalItemPriceIncVat = unitItemPriceIncVat * count;
        const totalItemPrice = unitItemTotalPrice * count;

        for (let i = 0; i < repaymentCount; i++) {
          const installmentNumber = i + 1;
          const [installmentAmount, installmentAmountIncVat] =
            FormHelper.calculatePrice(
              totalItemPrice,
              totalItemPriceIncVat,
              repaymentCount,
              fractions,
              installmentNumber,
            );

          this.installments[i].installmentAmountIncVat +=
            installmentAmountIncVat;
          this.installments[i].installmentAmount += installmentAmount;
        }
      }

      if (
        this.discountCode !== null &&
        this.discountCode.type === DiscountType.CASH
      ) {
        let discountAmount = this.order.discount || 0;
        let discountAmountWithoutVat = this.order.discountWithoutVat || 0;

        for (let i = 0; i < repaymentCount; i++) {
          const installmentNumber = i + 1;

          const [
            installmentDiscountAmountWithoutVat,
            installmentDiscountAmount,
          ] = FormHelper.calculatePrice(
            discountAmountWithoutVat,
            discountAmount,
            repaymentCount,
            fractions,
            installmentNumber,
          );

          this.installments[i].installmentAmountIncVat -=
            installmentDiscountAmount;
          this.installments[i].installmentAmount -=
            installmentDiscountAmountWithoutVat;
          this.installments[i].savedAmount += installmentDiscountAmount;
        }
      }

      if (this.shippingItem && !this.order.isEligibleForFreeShipping) {
        if (
          this.shippingItem.metadata &&
          this.shippingItem.metadata.is_disable_repayment
        ) {
          return;
        }
        let shippingAmountIncVat = NumberHelper.round(
          this.shippingItem.currentPriceIncVat,
        );
        let shippingAmount = NumberHelper.round(this.shippingItem.currentPrice);

        for (let i = 0; i < repaymentCount; i++) {
          const installmentNumber = i + 1;

          const [installmentShippingAmount, installmentShippingAmountIncVat] =
            FormHelper.calculatePrice(
              shippingAmount,
              shippingAmountIncVat,
              repaymentCount,
              fractions,
              installmentNumber,
            );

          this.installments[i].installmentAmountIncVat +=
            installmentShippingAmountIncVat;
          this.installments[i].installmentAmount += installmentShippingAmount;
        }
      }
    },

    validateMinMaxOrderValue: function (): boolean {
      var orderValue = {
        min: this.formResource.form_settings.min_order_value,
        max: this.formResource.form_settings.max_order_value,
      } as { [key: string]: any };

      Object.entries(orderValue).forEach(([key, price]) => {
        if (this.formResource.settings.currency !== this.formData.currency) {
          orderValue[key] =
            price !== null && price !== undefined
              ? (FormHelper.toSourceCurrency(
                  price,
                  this.formData.currency,
                  this.serviceLocator.exchangeRateProvider,
                ) ?? null)
              : null;

          if (orderValue[key] !== null && orderValue[key] !== undefined) {
            orderValue[key] = NumberHelper.round(Number(orderValue[key]));
          }
        }
      });

      if (
        orderValue.min !== null &&
        this.order.totalPriceIncludingVat < orderValue.min
      ) {
        this.addFormErrorMessage(
          this.$t("form.min_order_value") +
            orderValue.min +
            " " +
            this.formData.currency,
        );

        return false;
      }

      if (
        orderValue.max !== null &&
        this.order.totalPriceIncludingVat > orderValue.max
      ) {
        this.addFormErrorMessage(
          this.$t("form.max_order_value") +
            orderValue.max +
            " " +
            this.formData.currency,
        );

        return false;
      }

      return true;
    },

    processShowShippingMethods(): void {
      const item = this.selectedFormItemsWithCount().find(
        (selectedFormItemWithCount) => {
          return selectedFormItemWithCount.formItem.isPhysicalItem;
        },
      );

      this.showShippingMethods = item !== undefined;
      this.showShippingMethodsByItem = item !== undefined;
      this.isPhysicalItemSelected = item !== undefined;
    },

    selectedFormItemsWithCount(): SelectedFormItemWithCount[] {
      const selectedFormItemWithCount: SelectedFormItemWithCount[] = [];

      for (const selectedItem of this.formData.selected_items) {
        if (!selectedItem.selected) {
          continue;
        }

        if (selectedItem.count <= 0) {
          continue;
        }

        const formItem = this.formItems.find((item) => {
          return item.id === selectedItem.id;
        });

        if (formItem === undefined) {
          continue;
        }

        selectedFormItemWithCount.push({
          formItem: formItem,
          count: selectedItem.count,
        });
      }

      for (const selectedItem of this.formData.order_bump_selected_items) {
        if (!selectedItem.selected) {
          continue;
        }

        if (selectedItem.count <= 0) {
          continue;
        }

        const orderBump = this.orderBumps.find((item) => {
          return item.formItem.id === selectedItem.id;
        });

        if (orderBump === undefined) {
          continue;
        }

        selectedFormItemWithCount.push({
          formItem: orderBump.formItem,
          count: selectedItem.count,
        });
      }

      return selectedFormItemWithCount;
    },

    onStripeElements(elements: any) {
      this.stripeElements = elements;
    },

    determinateCurrency(country: string | null): void {
      this.formData.currency =
        this.serviceLocator.currencyDeterminer.determinate(
          this.formData.currency,
          country || this.formResource.user_country || "CZ",
        );

      this.localeLoader.changeCurrency(this.formData.currency);
    },

    determinateCurrencyByCountry(country: string | null): void {
      this.formData.currency =
        this.serviceLocator.countryCurrencyDeterminer.determinate(
          this.formData.currency,
          country || this.formResource.user_country || "CZ",
        );

      this.localeLoader.changeCurrency(this.formData.currency);
    },

    async onSubmit(event: Event): Promise<boolean> {
      event.preventDefault();
      this.isSubmitting = true;

      const formEle = this.$refs.form as InstanceType<
        typeof ValidationObserver
      > &
        HTMLElement;
      const promise = formEle.validate();

      this.$nextTick(function () {
        promise.then(this.processValidation);
      });

      return false;
    },

    processValidation(success: boolean): void {
      if (!success) {
        this.rollToError();

        return;
      }

      if (this.formData.honeypot) {
        this.addFormErrorMessage("form.server_error.honeypot_detected");

        return;
      }

      const hasPhysicalItem = this.selectedFormItemsWithCount().some(
        (item) => item.formItem.isPhysicalItem,
      );

      if (
        hasPhysicalItem &&
        this.formResource.shipping_methods.length > 0 &&
        this.formData.shipping_method_id === null
      ) {
        this.addFormErrorMessage("shipping_method.validations.missing");
        return;
      }

      this.processForm();
    },

    rollToError(): void {
      setTimeout(() => {
        const formEle = this.$refs.form as InstanceType<
          typeof ValidationObserver
        > &
          HTMLElement;
        const errors = Object.entries(formEle.errors)
          .map(([key, value]) => {
            return {
              key: key,
              value: value,
            };
          })
          .filter((error) => {
            return (
              error.hasOwnProperty("value") &&
              error.hasOwnProperty("key") &&
              error.value.length > 0
            );
          })
          .sort((a, b) => {
            const elA = formEle.refs[a["key"]].$el;
            const elB = formEle.refs[b["key"]].$el;

            return (
              elA.getBoundingClientRect().y - elB.getBoundingClientRect().y
            );
          });

        const element = formEle.refs[errors[0].key].$el;

        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);

      this.isSubmitting = false;
    },

    async processForm(): Promise<void> {
      if (!this.validateMinMaxOrderValue()) {
        return;
      }

      const discountCodePromise = this.tryToApplyDiscountCode();
      const setupGa4Promise = this.setupGa4IfNeeded();
      const setupGaPromise = this.setupGaIfNeeded();

      await (async () => {
        this.formData.tracking.browser.userAgent = navigator.userAgent;
        this.formData.tracking.browser.referrer = document.referrer;
        this.formData.tracking.browser.language = navigator.language;
        this.formData.tracking.browser.cookieEnabled = navigator.cookieEnabled;
        this.formData.tracking.browser.doNotTrack = navigator.doNotTrack;

        this.formData.tracking.facebook.fbp = getCookieValueByName("_fbp");
        this.formData.tracking.facebook.fbc = getCookieValueByName("_fbc");

        this.formData.tracking.ga4["gclid"] =
          UrlParser.getInstance().getParam("gclid");

        this.formData.tracking.fapi.promoCode =
          UrlParser.getInstance().getParam("fr_code") ||
          UrlParser.getInstance().getParam("a_box") ||
          UrlParser.getInstance().getParam("promoCode") ||
          UrlParser.getInstance().getParam("promo_Code") ||
          getCookieValueByName("fr_code") ||
          getCookieValueByName("promoCode") ||
          getCookieValueByName("fapiPromoCode");
      })();

      await discountCodePromise;
      await setupGaPromise;
      await setupGa4Promise;

      const payload: any = await this.sendDataToFapi();

      if (!payload.success) {
        console.error(payload.error);

        this.addFormErrorMessage(payload.error);

        return;
      }

      const response = payload.response;

      this.onAfterOrderCreated(response);
      const conversionPromise = this.serviceLocator.statisticsReporter
        .incrementConversion(this.formData)
        .catch((e) => {
          console.warn(e);
        });
      const applyGaPromise = this.applyGaIfNeeded(response);

      await this.processTwisto(response);
      await applyGaPromise;
      await conversionPromise;

      const nonStripePaymentElementTypes = [
        "wire",
        "cash",
        "collect on delivery",
      ];
      if (
        this.stripeElements !== null &&
        this.formData.payment_type !== null &&
        !nonStripePaymentElementTypes.includes(this.formData.payment_type)
      ) {
        const { error: submitError } = await this.stripeElements.submit();
        if (submitError) {
          const errorMessage =
            submitError.message ?? "An unknown error occurred.";
          this.addFormErrorMessage(errorMessage);
          this.isSubmitting = false;
          return;
        }
      }
      await this.confirmPayment(response);
      await this.redirectToNextUrl(response);
    },

    async confirmPayment(response: any): Promise<void> {
      if (this.stripeElements === null || this.stripe === null) {
        return;
      }

      if (response.stripe === null) {
        return;
      }

      if (response.stripe.client_secret === null) {
        return;
      }

      if (response.stripe.intent_type === "payment_intent") {
        const result = await this.stripe.confirmPayment({
          elements: this.stripeElements,
          clientSecret: response.stripe.client_secret,
          confirmParams: {
            return_url: response.stripe.next_url,
            payment_method_data: {
              billing_details: {
                address: {
                  country: response.address?.country,
                  city: response.address?.city,
                  line1: response.address?.street,
                  postal_code: response.address?.zip,
                },
                name: response.first_name + " " + response.last_name,
                email: response.email,
                phone: response.phone,
              },
            },
          },
        });

        this.redirectToUrl(response.stripe.next_url);

        return;
      }

      const result = await this.stripe.confirmSetup({
        elements: this.stripeElements,
        clientSecret: response.stripe.client_secret,
        confirmParams: {
          return_url: response.stripe.next_url,
          payment_method_data: {
            billing_details: {
              address: {
                country: response.address.country,
                city: response.address.city,
                line1: response.address.street,
                postal_code: response.address.zip,
              },
              name: response.first_name + " " + response.last_name,
              email: response.email,
              phone: response.phone,
            },
          },
        },
        redirect: "if_required",
      });
    },

    async setupGaIfNeeded(): Promise<void> {
      if (
        !this.formResource.enable_google_analytics_support ||
        // @ts-ignore
        !window.__fapi_ga_loaded
      ) {
        return;
      }

      return new Promise((resolve) => {
        // @ts-ignore
        const ga = window.__fapi_ga;
        ga("create", this.formResource.google_analytics_tracking_id);
        ga("require", "ec");

        ga((tracker: any) => {
          this.formData.google_analytics_client_id = tracker.get("clientId");
          resolve();
        });
      });
    },

    async setupGa4IfNeeded(): Promise<void> {
      // @ts-ignore
      if (typeof gtag === "undefined") {
        return;
      }

      // @ts-ignore
      if (typeof gtag !== "function") {
        return;
      }

      if (!this.formResource.enable_google_analytics_support) {
        return;
      }

      const ga4Keys = [
        "client_id",
        "campaign_id",
        "campaign_source",
        "campaign_medium",
        "campaign_name",
        "campaign_term",
        "campaign_content",
      ];

      ga4Keys.forEach((key) => {
        // @ts-ignore
        gtag(
          "get",
          this.formResource.google_analytics_tracking_id,
          key,
          (value: string) => {
            this.formData.tracking.ga4[key] = value;
          },
        );
      });
    },

    applyGaIfNeeded(payload: any): Promise<any> | void {
      if (
        !this.formResource.enable_google_analytics_support ||
        // @ts-ignore
        !window.__fapi_ga_loaded
      ) {
        return;
      }

      return new Promise((resolve) => {
        // @ts-ignore
        const ga = window.__fapi_ga;
        const googleAnalyticsTransactionData =
          payload.google_analytics_transaction_data;

        if (!ga || !googleAnalyticsTransactionData) {
          return resolve(payload);
        }

        ga("set", "&cu", googleAnalyticsTransactionData.currency);

        for (const product of googleAnalyticsTransactionData.products) {
          ga("ec:addProduct", product);
        }

        ga(
          "ec:setAction",
          "purchase",
          googleAnalyticsTransactionData.transaction,
        );

        ga("send", "pageview", {
          hitCallback: function () {
            return resolve(payload);
          },
        });
      });
    },

    async sendDataToFapi(): Promise<object> {
      const data: any = { ...this.formData };
      data.ic = data.ico;
      delete data.ico;
      data.items = [];
      data.inline_payment = false;
      data.stripe_payment_elements = !!this.stripeElements;
      data.security_token = this.securityToken;

      for (const selectedFormItemWithCount of this.selectedFormItemsWithCount()) {
        data.items.push({
          item_template: selectedFormItemWithCount.formItem.itemTemplateId,
          count: selectedFormItemWithCount.count,
        });
      }

      if (this.formData.shipping_address.first_name) {
        data.shipping_address.name = this.formData.shipping_address.first_name;
        delete data.shipping_address.first_name;
      }

      if (this.formData.shipping_address.last_name) {
        data.shipping_address.surname =
          this.formData.shipping_address.last_name;
        delete data.shipping_address.last_name;
      }

      if (!data.with_shipping_address) {
        delete data.shipping_address;
      }

      if (!this.formData.full_invoice) {
        data.simplified = true;
      }

      if (this.paymentMethodItem) {
        data.payment_method_id =
          this.paymentMethodItem.paymentMethodResource.id;
      }

      if (this.formData.form_first_interaction !== null) {
        const now = new Date();
        this.formData.form_fill_duration =
          (now.valueOf() - this.formData.form_first_interaction.valueOf()) /
          1000;
        data.form_fill_duration = this.formData.form_fill_duration;
        data.form_first_interaction = formatDateTimeOrNull(
          this.formData.form_first_interaction,
        );
      }

      if (data.form_loaded) {
        data.form_loaded = formatDateTimeOrNull(this.formData.form_loaded);
      }

      return this.serviceLocator.createOrderRequest
        .create(data)
        .catch((error) => {
          console.error(error);
          return { success: false, error: "form.server_error.default" };
        });
    },

    processTwisto(payload: any): Promise<any> {
      return new Promise((resolve) => {
        const twistoData = payload.twisto_payload;
        const twisto = this.serviceLocator.twisto;

        if (!twistoData || !twisto.isInitialized()) {
          return resolve(payload);
        }

        twisto.check(twistoData, payload, resolve);
      });
    },

    redirectToUrl(url: string): void {
      if (window.top !== null && window.top !== window.self) {
        window.top.location.href = url;
      } else {
        window.location.href = url;
      }
    },

    redirectToNextUrl(payload: any): Promise<any> {
      return new Promise((resolve) => {
        //todo: hotfix, safari broke iframe and gateway popup not show
        const isSafari =
          navigator.vendor &&
          navigator.vendor.indexOf("Apple") > -1 &&
          navigator.userAgent &&
          navigator.userAgent.indexOf("CriOS") == -1 &&
          navigator.userAgent.indexOf("FxiOS") == -1;

        if (payload.stripe !== null && this.stripe !== null) {
          this.redirectToUrl(payload.next_url);

          return resolve(payload);
        }

        if (
          payload.inline_payment_gateway &&
          UrlParser.getInstance().isHttps() &&
          !isSafari &&
          this.formData.payment_type !== "apple pay"
        ) {
          ScriptsLoader.loadScript(
            "https://gw.sandbox.gopay.com/gp-gw/js/embed.js",
            () => {
              try {
                // @ts-ignore
                _gopay.checkout({
                  gatewayUrl: payload.next_url,
                });
              } catch (error) {
                this.redirectToUrl(payload.next_url);
              }

              resolve(payload);
            },
          );

          return resolve(payload);
        }

        if (payload.gateway_post_data !== null) {
          PostRedirect(payload.next_url, JSON.parse(payload.gateway_post_data));

          return resolve(payload);
        }

        if (payload.next_url !== null) {
          this.redirectToUrl(payload.next_url);

          return resolve(payload);
        }

        return resolve(payload);
      });
    },

    addFormErrorMessage(message: string): void {
      this.formError = message;
      this.isSubmitting = false;

      setTimeout(() => {
        this.formError = null;
      }, 7000);
    },

    async delay(ms: number): Promise<void> {
      await new Promise<void>((resolve) => setTimeout(() => resolve(), ms));
    },

    async recalculateItems(): Promise<void> {
      if (this.calculationRunning) {
        await this.delay(100);

        return await this.recalculateItems();
      }

      this.calculationRunning = true;

      const applyDiscount =
        this.discountCode !== null &&
        this.discountCode.type === DiscountType.PERCENT;
      const isCompany = this.formData.ico !== null;
      const country = this.formData.address.country;
      const shippingCountry = this.formData.shipping_address.country;
      const currency = this.formData.currency;
      const domesticCurrency = this.formResource.settings.currency;
      const exchangeRateProvider = this.serviceLocator.exchangeRateProvider;
      const vatPayer = this.serviceLocator.isVatPayer.isVatPayer(country);
      const isReverseCharge =
        this.serviceLocator.reverseChargeDeterminer.detectReverseCharge(
          vatPayer,
          country,
          this.formData.shipping_address.country,
          this.formData.ico,
          this.formData.dic,
          this.formData.ic_dph,
        );
      const isTaxExempt = this.serviceLocator.isTaxExempt.isTaxExempt(
        vatPayer,
        country,
        this.formData.shipping_address.country,
        this.formResource.reverse_charge,
      );

      for (const item of this.formItems) {
        const itemVat =
          this.serviceLocator.itemVatDeterminer.determinateItemVat(
            item,
            shippingCountry ?? country,
            await isReverseCharge,
            isTaxExempt,
            isCompany,
          );

        await item.recalculate(
          itemVat,
          currency,
          domesticCurrency,
          exchangeRateProvider,
          vatPayer,
          this.formData.repayment,
          this.formData.repayment_count,
        );

        if (applyDiscount) {
          item.applyDiscountCode(this.discountCode as DiscountCode);
        } else {
          item.resetDiscount();
        }
      }

      for (const upsellItem of this.upsellItems) {
        const itemVat =
          this.serviceLocator.itemVatDeterminer.determinateItemVat(
            upsellItem,
            country,
            await isReverseCharge,
            isTaxExempt,
            isCompany,
          );

        await upsellItem.recalculate(
          itemVat,
          currency,
          domesticCurrency,
          exchangeRateProvider,
          vatPayer,
          this.formData.repayment,
          this.formData.repayment_count,
        );

        if (applyDiscount) {
          upsellItem.applyDiscountCode(this.discountCode as DiscountCode);
        } else {
          upsellItem.resetDiscount();
        }
      }

      for (const orderBump of this.orderBumps) {
        const itemVat =
          this.serviceLocator.itemVatDeterminer.determinateItemVat(
            orderBump.formItem,
            country,
            await isReverseCharge,
            isTaxExempt,
            isCompany,
          );

        await orderBump.formItem.recalculate(
          itemVat,
          currency,
          domesticCurrency,
          exchangeRateProvider,
          vatPayer,
          this.formData.repayment,
          this.formData.repayment_count,
        );

        if (applyDiscount) {
          orderBump.formItem.applyDiscountCode(
            this.discountCode as DiscountCode,
          );
        } else {
          orderBump.formItem.resetDiscount();
        }
      }

      const shippingVatRate = this.getHighestVatRateForShipping();

      for (const shippingMethod of this.shippingMethods) {
        await shippingMethod.updatePrices(
          currency,
          domesticCurrency,
          exchangeRateProvider,
          shippingVatRate,
        );
      }

      if (this.collectOnDeliveryPaymentMethodItem !== null) {
        await this.collectOnDeliveryPaymentMethodItem.updatePrices(
          currency,
          domesticCurrency,
          exchangeRateProvider,
          shippingVatRate,
        );
      }

      this.calculationRunning = false;
      this.toggleShippingMethods = true;
    },

    calculateRoundItem(
      totalPrice: number,
      totalPriceIncludingVat: number,
      disableRepaymentTotalPrice: number,
      disableRepaymentTotalPriceIncVat: number,
      installments: InstallmentItem[],
    ): void {
      const prices = FormHelper.calculateRoundItem(
        this.getPrices(),
        totalPriceIncludingVat,
        totalPrice,
        disableRepaymentTotalPrice,
        disableRepaymentTotalPriceIncVat,
        this.formData.currency,
        this.formResource,
        installments,
      );

      this.order.totalPrice = prices.totalPrice;
      this.order.totalPriceIncludingVat = prices.totalPriceIncludingVat;
      this.order.disableRepaymentTotalPrice = prices.disableRepaymentTotalPrice;
      this.order.disableRepaymentTotalPriceIncVat =
        prices.disableRepaymentTotalPriceIncVat;
      this.installments = prices.installments;
    },

    getPrices(): { [key: string]: number } {
      const prices = FormHelper.getPrices(this.selectedFormItemsWithCount());

      if (this.shippingItem) {
        const key = String(this.shippingItem.currentVat);

        if (!prices.hasOwnProperty(key)) {
          prices[key] = 0;
        }

        prices[key] += this.shippingItem.currentPriceIncVat;
      }

      return prices;
    },

    getHighestVatRateForShipping(): number {
      let vatRate = null;

      for (const item of this.selectedFormItemsWithCount()) {
        if (item.formItem.isElectronicallySuppliedService) {
          continue;
        }

        const skipTypes = [
          ItemType.SHIPPING,
          ItemType.EXCLUDING_VAT,
          ItemType.PAYMENT_METHOD,
        ];

        if (
          item.formItem.type !== null &&
          skipTypes.includes(item.formItem.type)
        ) {
          continue;
        }

        if (vatRate === null || vatRate < item.formItem.currentVat) {
          vatRate = item.formItem.currentVat;
        }
      }

      if (vatRate === null) {
        return Number(this.formResource.settings.vat);
      }

      return vatRate;
    },

    async loadStripeIfNeeded(retryCount: number = 0): Promise<void> {
      if (!this.formResource.stripe?.public_key) {
        return;
      }

      try {
        this.stripe = await loadStripe(this.formResource.stripe?.public_key, {
          locale: this.$root.$i18n.locale as StripeElementLocale,
        });
      } catch (e) {
        this.stripe = null;
        console.warn(
          "Unable to load script Stripe [" +
            retryCount +
            "]. Retrying... Error: " +
            e,
        );
      }

      if (this.stripe !== null) {
        return;
      }

      if (retryCount > 5) {
        console.error("Unable to load script Stripe [" + retryCount + "].");

        throw new Error("Unable to load script Stripe [" + retryCount + "].");
      }

      await new Promise((resolve) => setTimeout(resolve, 50));
      await this.loadStripeIfNeeded(retryCount + 1);
    },

    onAfterOrderCreated(orderData: any): void {
      const event = new CustomEvent("fapiFormOnAfterOrderCreated", {
        detail: {
          orderData: orderData,
        },
      });

      document.dispatchEvent(event);
    },

    triggerPaymentMethods(newValue: OrderPrices): void {
      if (newValue.totalPriceIncludingVat <= 0.0) {
        const allowedPaymentTypes = ["gpay", "apple pay"];
        const hasGpayOrApplePay = this.formResource.payment_methods.some(
          (method) => allowedPaymentTypes.includes(method.payment_type),
        );

        if (
          this.stripe &&
          this.formResource.allow_gopay_recurring_payments &&
          this.formResource.allow_recurring &&
          hasGpayOrApplePay
        ) {
          this.paymentMethodChanged({
            payment_type: "credit card",
            bank: null,
            payment_method_item: null,
          });

          this.togglePaymentMethods = true;
        } else {
          this.paymentMethodChanged({
            payment_type: "wire",
            bank: "wire",
            payment_method_item: null,
          });

          this.togglePaymentMethods = false;
        }
      } else {
        this.togglePaymentMethods = true;
      }
    },

    reloadPage() {
      location.reload();
    },
  },

  watch: {
    order: {
      handler(orderPrices: OrderPrices) {
        this.triggerPaymentMethods(orderPrices);
      },
      deep: true,
    },
  },

  async created(): Promise<void> {
    this.formData.full_invoice = !this.formResource.allow_simplified;
    this.determinateCurrencyByCountry(this.formResource.default_country);
    this.determinateCurrency(null);

    const formItemBuilder = new FormItemBuilder(this.formResource);

    formItemBuilder.build();

    this.itemGroups = formItemBuilder.itemGroups;
    this.formItems = formItemBuilder.formItems;
    this.upsellItems = formItemBuilder.upsellItems;
    this.orderBumps = formItemBuilder.orderBumps;
    this.shippingMethods = formItemBuilder.shippingMethods;
    this.collectOnDeliveryPaymentMethodItem =
      formItemBuilder.collectOnDeliveryPaymentMethodFormItem;

    if (this.formResource.allow_recurring) {
      this.allowedPeriods = FormHelper.sortAllowedPeriods(
        FormHelper.getAllowedPeriods(
          this.formItems,
          this.formResource.period as string,
        ),
      );

      this.formData.period = this.formResource.period;
    }

    if (this.allowedPeriods.length > 0) {
      this.formData.period = this.allowedPeriods[0];
    }

    await this.recalculateItems();
    await this.recalculateOrder();
    this.triggerPaymentMethods(this.order);

    if (this.formResource.enable_google_analytics_support) {
      import("@/module/Utils/GaInstaller").then(
        ({ GaInstaller: GaInstaller }) => {
          GaInstaller();
        },
      );
    }
  },

  mounted(): void {
    this.loadStripeIfNeeded();

    this.$nextTick(() => {
      if (this.formResource.affilbox_tracking_code) {
        TrackingCodeLoader(this.$el, this.formResource.affilbox_tracking_code);
      }

      this.formData.pending = Boolean(
        this.formResource.allow_upsells &&
          !this.formResource.allow_new_upsell_process,
      );
      this.formData.upsells_enabled = this.formData.pending;
      this.formData.form_url = window.location.toString();
      this.formData.form_title = document.title;
      // @ts-ignore
      window._ssaq = window._ssaq || [];
      // @ts-ignore
      window._ssaq.push([
        "getSessionId",
        (sessionId: string | null) => {
          this.formData.session_id = sessionId;
        },
      ]);

      this.serviceLocator.twisto.init();
      this.serviceLocator.statisticsReporter.incrementVisits();
      this.$el.addEventListener("input", () => {
        if (this.formData.form_first_interaction !== null) {
          return;
        }

        this.formData.form_first_interaction = new Date();
      });
    });

    const mountedEvent = new CustomEvent("fapiFormMounted", {
      detail: { formWrapper: this.$el },
    });
    document.dispatchEvent(mountedEvent);

    if (
      "IntersectionObserver" in window &&
      "IntersectionObserverEntry" in window &&
      "intersectionRatio" in window.IntersectionObserverEntry.prototype
    ) {
      setTimeout(() => {
        this.showFloat = true;
      }, 500);
    }

    if (
      this.formResource.foxentry?.token !== undefined &&
      this.formResource.foxentry.token !== null
    ) {
      ScriptsLoader.loadScript("https://cdn.foxentry.cz/lib", () => {
        //@ts-ignore
        window.Foxentry = new FoxentryBase(this.formResource.foxentry.token);
      });
    }
    this.orderIntentSubmitted = this.skipToSecondStep();

    this.eventCallback = () => {
      this.hasLoadingError = true;
    };

    EventBus.$on("critical-component-failed", this.eventCallback);
  },

  beforeDestroy(): void {
    if (this.eventCallback) {
      EventBus.$off("critical-component-failed", this.eventCallback);
    }
  },
});
</script>

<style lang="scss"></style>
